import { Api } from 'lib';

/**
 * Tag It (TIRI) ground-truth admin API.
 *
 * Thin wrappers over the shared `Api` client for the admin category/labeling endpoints used
 * by the TIRI menu. `{ silent: true }` so callers handle errors inline instead of the global
 * error toast.
 *
 * Note: `REACT_APP_API_URL` already ends in `/api`, so paths start at `/admin`.
 * Note: `Api.get`/`Api.post` return the response body as-is, and the backend's
 * `httpStatusSender` collapses a single-key `{ categories }` payload down to the raw array —
 * so these resolve directly to the array/object below, with no `.data` layer to unwrap.
 */

/** GET /api/admin/tiri/tagit/categories -> categories[] */
export const getTiriTagitCategories = () =>
  Api.get('/admin/tiri/tagit/categories', null, { silent: true });

/**
 * POST /api/admin/tiri/tagit/label -> TagItGroundTruth
 *
 * `tiriId` addresses direct Team Channel uploads and FanCam-derived items alike, so it is the
 * only key the endpoint takes.
 */
export const saveTiriTagitLabel = ({ tiriId, categoryId }) =>
  Api.post('/admin/tiri/tagit/label', { tiriId, categoryId }, null, { silent: true });

/**
 * POST /api/admin/tiri/list -> { data: TiriItem[] } (a page of up to 50, newest first)
 *
 * Unlike the older tiri endpoints above, this one and the summary below answer through
 * `ApiResponse.success`, so the rows arrive under `.data` — callers must unwrap.
 */
export const getTiriItems = (body) => Api.post('/admin/tiri/list', body, null, { silent: true });

/** GET /api/admin/tiri/summary -> { data: { total, tagit, rateit } } */
export const getTiriSummary = (teamId) =>
  Api.get(`/admin/tiri/summary${teamId ? `?teamId=${teamId}` : ''}`, null, { silent: true });

/**
 * Fan-facing Rate It endpoints, used by `screens/RateIt`.
 *
 * Unlike the admin wrappers above these are not admin-scoped, and two of them are readable by a
 * logged-out visitor — a referral link is overwhelmingly opened by someone who is not signed in.
 * `Api` sends the bearer token when there is one, so the same call serves both cases and the
 * server personalizes what it can.
 */

/**
 * GET /api/tiri/rateit/feed -> { items, nextCursor }
 *
 * A fresh RANDOM DRAW per call, not a page: `nextCursor` is always null, and two calls can
 * legitimately return overlapping items. Callers must dedupe by `_id` — see `useRateItFeed`.
 */
export const getRateItFeed = ({ limit, teamId } = {}) => {
  const search = new URLSearchParams();
  if (typeof limit === 'number') search.append('limit', String(limit));
  if (teamId) search.append('teamId', teamId);
  const qs = search.toString();

  return Api.get(`/tiri/rateit/feed${qs ? `?${qs}` : ''}`, null, { silent: true });
};

/**
 * GET /api/tiri/rateit/item/:id -> { item, myRating, canRate, reason }
 *
 * One moment by id, in exactly the shape the feed returns its items. This is how the referral
 * landing honours the brief's hard requirement that it show the EXACT shared moment: the feed is
 * a random draw that will usually not contain it.
 */
export const getRateItItem = (tiriId) =>
  Api.get(`/tiri/rateit/item/${tiriId}`, null, { silent: true });

/**
 * POST /api/tiri/rateit/submit ->
 *   { rating, alreadyRated, milestoneReached, milestoneCount, pointsAwarded,
 *     milestoneBreakdown }
 *
 * `milestoneBreakdown` is `{ tagit, rateit }` — what the completed cycle was made of. The
 * milestone modal is raised only when `tagit` is 0, since the cycle is shared with Tag It in
 * the app and this surface's modal speaks to a fan who has been rating.
 *
 * `shortCode` is the referral link the fan is rating under, and it is what attributes the rating
 * to that invite — a fan holds one referral per link they were sent, so without it the server has
 * no way to tell which of them this rating belongs to. Spread conditionally rather than passed as
 * `undefined`: an explicit `null` on the wire would fail the endpoint's Joi validation, where an
 * absent key is simply optional. Omitted, the server falls back to the fan's single active
 * referral if they have exactly one, and otherwise advances none.
 *
 * Authed. `pointsAwarded` is the unified TIRI participation payout — 0 unless this rating
 * completed a cycle, which Tag It's completions feed too. It used to be withheld on purpose
 * (the payout was silent, met on the fan's profile); product reversed that so both modules
 * announce it. This surface does not render it yet — see `components/RateItFeed`.
 *
 * Still carries NO referral figure. That is a different, larger number and is read separately
 * from `GET /tiri/rateit/referral/status`.
 */
export const submitRateIt = ({ tiriId, rating, shortCode }) =>
  Api.post('/tiri/rateit/submit', { tiriId, rating, ...(shortCode ? { shortCode } : {}) }, null, {
    silent: true
  });

/**
 * POST /api/tiri/view -> { data: { viewCount } }
 *
 * Counts one view of a moment: the server increments its `viewCount` and logs the matching
 * `tiri.rateit.view` Reach event in the same request, so the number on the card and admin
 * Insights' TIRI Reach agree. Authed — a Reach event needs a real user — so callers skip it for
 * a signed-out visitor.
 *
 * Answers through `ApiResponse.success`, so the count arrives under `.data`. Like every `Api`
 * call it never throws: a failure comes back as a body with no `data`.
 */
export const trackTiriView = ({ tiriId, module = 'rateit' }) =>
  Api.post('/tiri/view', { tiriId, module }, null, { silent: true });

/**
 * POST /api/tiri/rateit/referral/activate -> { status, reason, progress, points, referrer }
 *
 * Authed, idempotent. Takes only the link's shortCode — the referrer is read server-side from
 * the stored link, so there is deliberately nothing here for a client to name. Answers 200 for
 * every ordinary outcome, including the ones that award nothing, so callers should branch on
 * `status` rather than treating a non-2xx as the only failure.
 */
export const activateRateItReferral = ({ shortCode, source = 'web' }) =>
  Api.post('/tiri/rateit/referral/activate', { shortCode, source }, null, { silent: true });

/**
 * GET /api/deeplink/:shortCode -> { screen, params, sender?, referral? }
 *
 * PUBLIC, which is the whole reason the landing reads its banner from here: `sender.name` and
 * `referral.points` arrive in one call that a signed-out visitor can make, while every endpoint
 * carrying the points figure otherwise requires auth.
 *
 * Note this counts a click server-side, so call it once per landing rather than per render.
 */
export const resolveDeepLink = (shortCode) =>
  Api.get(`/deeplink/${shortCode}`, null, { silent: true });
